import { storagePlacementRepairHelp } from "./alignment-option-help";
import { MaintenanceHelp } from "./MaintenanceHelp";
import type { MaintenanceOperationStatus } from "./MaintenanceDialog";
import type { AutomaticAlignmentController } from "./hooks/useAutomaticAttachmentAlignment";
import { ManualAttachmentExecutionGate } from "./AutomaticAttachmentAlignmentPanel";
import { useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Loader2, Play, ScanSearch } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { AppBindingResponse } from "@/types/api";
import {
  useAsynchronousExecutionCommand,
  useAsynchronousExecutions,
  useAsynchronousExecutionDetail,
  useAttachmentStorageAlignmentPlanPreview,
  useStartAttachmentStorageAlignmentExecution,
  type AttachmentStorageAlignmentExecutionMode,
  type AttachmentStorageAlignmentExecutionOptions,
  type AttachmentStorageAlignmentPlanPreviewResponse,
} from "./hooks/useAppBindings";
import { formatDateTime } from "./attachment-state-ui";
import { AttachmentBatchScopeControls, estimateAttachmentBatchApiCalls, type AttachmentBatchRecordLimit } from "./AttachmentBatchScopeControls";
import { ActiveExecutionPanel } from "./ActiveExecutionPanel";
import {
  PLACEMENT_BUILTIN_PLACEHOLDERS,
  fixedPlacementSampleRecord,
  renderPlacementSample,
  type PlacementSampleTarget,
} from "./shared-externalize-placement-sample";

interface AttachmentStorageAlignmentSectionProps {
  binding: AppBindingResponse;
  storageDisplayName?: string;
  automaticAlignment: AutomaticAlignmentController;
  initialExecutionMode?: AttachmentStorageAlignmentExecutionMode;
  embedded?: boolean;
  onStatusChange?(status: MaintenanceOperationStatus): void;
  onShowAutomatic?(): void;
}

export function AttachmentStorageAlignmentSection({
  binding,
  storageDisplayName,
  automaticAlignment,
  initialExecutionMode = "diagnostic_only",
  onStatusChange,
  onShowAutomatic,
  embedded = false,
}: AttachmentStorageAlignmentSectionProps) {
  const [preview, setPreview] =
    useState<AttachmentStorageAlignmentPlanPreviewResponse | null>(null);
  const [executionOptions, setExecutionOptions] =
    useState<AttachmentStorageAlignmentExecutionOptions | null>(null);
  const [executionMode, setExecutionMode] =
    useState<AttachmentStorageAlignmentExecutionMode>(initialExecutionMode);
  const [lastStartedId, setLastStartedId] = useState("");
  const lastStarted = useAsynchronousExecutionDetail(lastStartedId, true);
  const [maxRecordCount, setMaxRecordCount] = useState<AttachmentBatchRecordLimit>(1000);
  const [continuationId, setContinuationId] = useState("");
  const latestDiagnostics = useAsynchronousExecutions({ kintoneAppId: binding.kintoneAppId, completedDiagnosticOnly: "true", limit: "1" });
  const latest = latestDiagnostics.items.find(item => item.appBindingId === binding.appBindingId && item.executionState === "completed");
  const latestDetail = useAsynchronousExecutionDetail(latest?.executionId ?? "");
  const completedDiagnosis = latestDetail.execution?.executionId === latest?.executionId && latestDetail.execution?.appBindingId === binding.appBindingId && latestDetail.execution?.executionState === "completed"
    ? latestDetail.execution : null;
  const diagnosis = completedDiagnosis?.attachmentStorageAlignmentDiagnosticResult;
  const latestManualHistory = useAsynchronousExecutions({ kintoneAppId: binding.kintoneAppId, completedManualAlignmentOnly: "true", limit: "1" });
  const latestManual = latestManualHistory.items.find(item => item.appBindingId === binding.appBindingId && item.executionState === "completed");
  const latestManualDetail = useAsynchronousExecutionDetail(latestManual?.executionId ?? "");
  const manualResults = [latestManualDetail.execution?.executionId === latestManual?.executionId ? latestManualDetail.execution : null, lastStarted.execution]
    .filter(item => item?.appBindingId === binding.appBindingId && item.executionState === "completed" && item.attachmentStorageAlignmentExecutionConfiguration?.executionMode === "mutating_alignment")
    .sort((a, b) => b!.createdAt.localeCompare(a!.createdAt) || b!.executionId.localeCompare(a!.executionId));
  const completedManual = manualResults[0] ?? null;
  const manualScope = completedManual?.recordProcessingScope;
  const manualHistoryVerified = !latestManualHistory.isLoading && !latestManualHistory.error && !latestManualDetail.isLoading && !latestManualDetail.error;
  const [operatorConfirmed, setOperatorConfirmed] = useState(false);
  const [placementTemplates, setPlacementTemplates] =
    useState<SharedStorageExternalizePlacementTemplates | null>(null);
  const planPreview = useAttachmentStorageAlignmentPlanPreview(
    binding.kintoneAppId,
  );
  const startExecution = useStartAttachmentStorageAlignmentExecution(
    binding.kintoneAppId,
  );
  const executionHistory = useAsynchronousExecutions({
    kintoneAppId: binding.kintoneAppId,
    activeOnly: "true",
    limit: "1",
  });
  const operationError = startExecution.error ?? executionHistory.error;
  useEffect(() => {
    onStatusChange?.({ pending: startExecution.isLoading, error: operationError, executionId: lastStartedId });
  }, [onStatusChange, startExecution.isLoading, operationError, lastStartedId]);
  const latestRefresh = latestDiagnostics.refresh;
  const manualRefresh = latestManualHistory.refresh;
  const activeExecution = executionHistory.items.find(
    (item) => item.appBindingId === binding.appBindingId &&
      ["queued", "running", "blocked", "cancel_requested"].includes(item.executionState),
  );
  const previousActiveId = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (previousActiveId.current && !activeExecution) { void latestRefresh(); void manualRefresh(); }
    previousActiveId.current = activeExecution?.executionId;
  }, [activeExecution?.executionId, latestRefresh, manualRefresh]);
  const cancelExecution = useAsynchronousExecutionCommand(
    binding.kintoneAppId,
    activeExecution?.executionKind ?? "attachment_storage_alignment",
    activeExecution?.executionId ?? "",
    "cancel",
  );
  const resumeExecution = useAsynchronousExecutionCommand(
    binding.kintoneAppId,
    activeExecution?.executionKind ?? "attachment_storage_alignment",
    activeExecution?.executionId ?? "",
    "resume",
  );
  const hasBlockers = Boolean(preview?.blockers.length);
  const diagnostic = executionMode === "diagnostic_only";
  const estimateRecordCount = Math.min(maxRecordCount, preview?.candidateRecordTotalCount ?? maxRecordCount);
  const previousExecutionId = diagnostic
    ? lastStarted.execution?.executionState === "completed" && lastStarted.execution.recordProcessingScope?.scanTerminalState === "record_limit_reached" && lastStarted.execution.attachmentStorageAlignmentExecutionConfiguration?.executionMode === "diagnostic_only"
      ? lastStartedId : completedDiagnosis?.recordProcessingScope?.scanTerminalState === "record_limit_reached" ? completedDiagnosis.executionId : undefined
    : manualHistoryVerified && manualScope?.scanTerminalState === "record_limit_reached" ? completedManual?.executionId : undefined;
  const startBlockedReason = !automaticAlignment.canStartManual
    ? "自動整合化の状態と手動実行の開始条件を確認してください。"
    : continuationId && continuationId !== previousExecutionId
      ? "選択した続行元を確認できません。結果を再取得するか、開始位置を先頭に戻してください。"
    : !preview
    ? null
    : executionHistory.error || executionHistory.isLoading
      ? "実行履歴を確認できるまで開始できません。"
    : planPreview.isLoading
      ? "対象の確認が終わるまで開始できません。"
    : activeExecution
      ? "進行中の実行が終わるまで開始できません。"
      : hasBlockers
        ? "開始できない条件を解消してから、もう一度確認してください。"
        : preview.fieldPlans.length === 0
          ? "対象のフィールドがありません。"
          : !diagnostic && !operatorConfirmed
            ? "内容を確認し、下のチェックを入れてください。"
            : null;
  const canStart = Boolean(preview && executionOptions && startBlockedReason === null);
  const [previewAction, setPreviewAction] = useState("対象フィールドの取得");
  const historyRefresh = executionHistory.refresh;
  useEffect(() => {
    if (!activeExecution) return;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout>;
    const update = async () => {
      await historyRefresh();
      if (!stopped) timer = setTimeout(() => void update(), 3000);
    };
    timer = setTimeout(() => void update(), 3000);
    return () => { stopped = true; clearTimeout(timer); };
  }, [activeExecution?.executionId, historyRefresh]);

  async function handlePreview(includeRecordCount = true) {
    setPreviewAction(includeRecordCount ? "候補レコード数の確認" : "実行内容の確認");
    try {
      const result = await planPreview.mutate({ includeFieldPlan: true, includeRecordCount });
      if (!preview || result.previewSnapshotId !== preview.previewSnapshotId) {
        setExecutionOptions(defaultExecutionOptions(result));
        setPlacementTemplates(result.sharedStorageExternalizePlacementDefaults ?? null);
        setOperatorConfirmed(false);
      }
      setPreview(result);
      startExecution.reset();
      await executionHistory.refresh();
      await latestRefresh();
      await manualRefresh();
    } catch {
      setPreview((current) => current ? { ...current, candidateRecordTotalCount: null, countedAt: null } : null);
    }
  }

  async function handleStart() {
    if (!preview || !executionOptions || !canStart || startExecution.isLoading) return;
    setOperatorConfirmed(false);
    const placementDefaults = preview.sharedStorageExternalizePlacementDefaults;
    const effectiveOptions = diagnostic ? defaultExecutionOptions(preview) : executionOptions;
    const effectiveTemplates = diagnostic && placementDefaults
      ? {
          rootFieldTemplate: placementDefaults.rootFieldTemplate,
          subtableFieldTemplate: placementDefaults.subtableFieldTemplate,
        }
      : placementTemplates;
    const sharedExternalize = Boolean(placementDefaults && effectiveTemplates) &&
      effectiveOptions.migrateStandardStorageFilesToExternalStorage;
    try {
      const started = await startExecution.mutate({
        executionMode,
        maxRecordCount,
        ...(continuationId ? { continuationFromExecutionId: continuationId } : {}),
        previewSnapshotId: preview.previewSnapshotId,
        targetFieldBindingKeys: preview.fieldPlans.map((field) => field.fieldBindingKey),
        executionOptions: effectiveOptions,
        ...(sharedExternalize
          ? {
            sharedStorageExternalizePlacement: placementDefaults && effectiveTemplates
              ? { ...placementDefaults, ...effectiveTemplates }
              : undefined,
            sharedStorageExternalizeWarningAccepted: true,
          }
          : {}),
        confirmedAt: new Date().toISOString(),
      });
      setLastStartedId(started.executionId);
      await executionHistory.refresh();
      await automaticAlignment.refresh();
      setPreview((current) => current ? { ...current, candidateRecordTotalCount: null, countedAt: null } : null);
      setOperatorConfirmed(false);
    } catch {
      // The start may have raced admission or committed before the response failed.
      await executionHistory.refresh();
      await automaticAlignment.refresh();
      /* The start hook retains the operation-specific failure and manual input. */
    }
  }

  return (
    <section className="space-y-4" aria-label="添付ファイル整合化">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          {!embedded ? <h2 className="text-lg font-semibold">添付ファイル整合化</h2> : null}
          <p className="mt-1 text-sm text-muted-foreground">
            アプリ設定とレコードやストレージの状態が整合しているか確認し、不整合を修正します。
          </p>
        </div>
        <Button asChild type="button" variant="outline" size="sm">
          <Link to={`/console/asynchronous-executions?executionKind=attachment_storage_alignment&kintoneAppId=${encodeURIComponent(binding.kintoneAppId)}`}>
            実行履歴を見る
          </Link>
        </Button>
      </div>

      {latestManualHistory.error || latestManualDetail.error || latestDiagnostics.error || latestDetail.error ? <p role="alert">過去の結果を確認できません。「過去の実行結果」を開いて再取得してください。</p> : null}
      {!activeExecution && onShowAutomatic && completedManual && manualScope && manualScope.scanTerminalState !== "in_progress" && automaticAlignment.state?.state === "disabled" ? <p className="text-sm">手動整合化が完了しました。<button type="button" className="underline" onClick={onShowAutomatic}>自動整合化の設定に戻る</button></p> : null}
      <details data-maintenance-options><summary className="cursor-pointer py-2 text-sm">過去の実行結果</summary>
      <div className="space-y-3 py-3">
      <Card role="region" aria-label="直近の手動整合化">
        <CardHeader><CardTitle className="text-base">直近の手動整合化</CardTitle></CardHeader>
        <CardContent className="space-y-2 text-sm">
          {latestManualHistory.error || latestManualDetail.error ? <div><AlertBox message={`手動整合化の完了状態を確認できません。${latestManualHistory.error ?? latestManualDetail.error}`} /><Button type="button" variant="outline" onClick={() => { void manualRefresh(); void latestManualDetail.refresh(); }}>手動整合化の結果を再取得</Button></div> : null}
          {completedManual && manualScope && manualScope.scanTerminalState !== "in_progress" ? <>
            <p>完了日時：{formatDateTime(completedManual.finishedAt)}</p>
            <p>{manualScope.scanTerminalState === "exhausted" ? "開始時点の対象範囲の走査が完了しました。続行対象はありません。" : `最大${manualScope.maxRecordCount}件の走査が完了しました。続きのレコードがあります。`}</p>
            <p>走査完了は全件の整合完了を意味しません。未解決レコードは自動整合化の状態で確認してください。</p>
            <p>走査レコード数：{manualScope.scannedRecordCount}件・開始位置 {manualScope.startAfterRecordId}・最終レコードID {manualScope.lastScannedRecordId}</p>
            <p className="text-muted-foreground">開始後に追加・更新されたレコードは、次の整合化で確認してください。{manualScope.scanTerminalState === "record_limit_reached" ? "続きのレコードが後で削除された場合は、次回は0件で完了することがあります。" : ""}</p>
            <div className="flex flex-wrap items-center gap-3">
              <Link className="underline" to={`/console/asynchronous-executions/${completedManual.executionId}?kintoneAppId=${encodeURIComponent(binding.kintoneAppId)}`}>手動整合化の詳細を見る</Link>
              <Button type="button" variant="outline" onClick={() => { setExecutionMode("mutating_alignment"); setContinuationId(""); setOperatorConfirmed(false); }}>先頭からの再実行を準備</Button>
              <a className="underline" href="#automatic-attachment-alignment-heading" onClick={onShowAutomatic ? event => { event.preventDefault(); onShowAutomatic(); } : undefined}>自動整合化の再有効化を確認</a>
            </div>
          </> : <p>{latestManualHistory.isLoading || latestManualDetail.isLoading ? "手動整合化の完了状態を確認しています。" : latestManualHistory.error || latestManualDetail.error ? "手動整合化の完了状態は未確認です。" : "完了した手動整合化はありません。"}</p>}
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-base">直近の完了診断</CardTitle></CardHeader>
        <CardContent className="space-y-2 text-sm">
          {latestDiagnostics.error || latestDetail.error ? <div><AlertBox message={`診断の取得に失敗しました。${latestDiagnostics.error ?? latestDetail.error}`} /><Button variant="outline" onClick={() => { void latestRefresh(); void latestDetail.refresh(); }}>完了診断を再取得</Button></div> : null}
          {diagnosis ? <>
            <p>診断日時：{formatDateTime(completedDiagnosis.finishedAt)}</p>
            <p>走査レコード数：{diagnosis.scannedRecordCount}件 / 対応候補数：{diagnosis.findingCount}件 / 要確認件数：{diagnosis.manualReviewReasonCounts.reduce((total, item) => total + item.count, 0)}件</p>
            <p>{diagnosis.scanTerminalState === "record_limit_reached" ? "選択範囲の診断が完了しました。全アプリの診断は未完了です。" : "開始時の対象範囲を全件走査済みです。"} 最大{completedDiagnosis.recordProcessingScope?.maxRecordCount ?? diagnosis.recordScanLimit}件・開始位置 {completedDiagnosis.recordProcessingScope?.startAfterRecordId ?? "0"}・最終レコードID {diagnosis.processedRecordIdPointer ?? "なし"}</p>
            <Link className="underline" to={`/console/asynchronous-executions/${completedDiagnosis.executionId}?kintoneAppId=${encodeURIComponent(binding.kintoneAppId)}`}>詳細を見る</Link>
          </> : <p>{latestDiagnostics.isLoading || latestDetail.isLoading ? "完了診断を確認しています。" : latestDiagnostics.error || latestDetail.error ? "完了診断を確認できません。" : "未診断"}</p>}
        </CardContent>
      </Card>
      </div></details>
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">事前確認と開始</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 p-3 sm:p-6">
          {activeExecution ? (
            <ActiveExecutionPanel kintoneAppId={binding.kintoneAppId}
              execution={activeExecution}
              busy={resumeExecution.isLoading || cancelExecution.isLoading}
              stateVerified={!executionHistory.isLoading && !executionHistory.error}
              stateReadFailed={Boolean(executionHistory.error)}
              onRefresh={executionHistory.refresh}
              onResume={() => resumeExecution.mutate({ expectedRevision: activeExecution.revision, expectedExecutionState: "blocked", blockerResolutionConfirmed: true, reason: "管理者が阻害要因の解消を確認" }).then(() => executionHistory.refresh()).catch(() => { /* The command hook retains its failure. */ })}
              onCancel={() => cancelExecution.mutate({ expectedRevision: activeExecution.revision, expectedExecutionState: activeExecution.executionState, reason: "管理者が取消を要求" }).then(() => executionHistory.refresh()).catch(() => { /* The command hook retains its failure. */ })}
            />
          ) : null}
          <ManualAttachmentExecutionGate controller={automaticAlignment} />
          {planPreview.error ? <AlertBox message={`${previewAction}に失敗しました。${planPreview.error}`} /> : null}
          {startExecution.error ? <AlertBox message={`実行開始に失敗しました。${startExecution.error}`} /> : null}
          {executionHistory.error ? (
            <div className="space-y-2">
              <AlertBox message={`実行履歴の取得に失敗しました。${executionHistory.error}`} />
              <Button type="button" variant="outline" onClick={() => void executionHistory.refresh()} disabled={executionHistory.isLoading}>実行履歴を再取得</Button>
            </div>
          ) : null}
          {cancelExecution.error ? <AlertBox message={`取消に失敗しました。${cancelExecution.error}`} /> : null}
          {resumeExecution.error ? <AlertBox message={`再開に失敗しました。${resumeExecution.error}`} /> : null}
          {preview && executionOptions ? (
            <PreviewPanel
              preview={preview}
              onPreview={() => void handlePreview()}
              previewLoading={planPreview.isLoading}
              options={executionOptions}
              executionMode={executionMode}
              modeId={binding.modeId}
              storageDisplayName={storageDisplayName}
              operatorConfirmed={operatorConfirmed}
              placementTemplates={placementTemplates}
              onChangePlacementTemplates={(templates) => {
                setPlacementTemplates(templates);
                setContinuationId("");
                setOperatorConfirmed(false);
              }}
              onChangeMode={(mode) => {
                setExecutionMode(mode);
                setContinuationId("");
                setOperatorConfirmed(false);
              }}
              onChangeOptions={(options) => {
                setExecutionOptions(options);
                setContinuationId("");
                setOperatorConfirmed(false);
              }}
              onChangeConfirmed={setOperatorConfirmed}
            />
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">{planPreview.isLoading ? "対象フィールドを取得しています。" : "実行内容を確認してから開始してください。"}</p>
              <Button type="button" variant="outline" onClick={() => void handlePreview(false)} disabled={planPreview.isLoading}>実行内容を確認</Button>
              <ExecutionModeChoice value={executionMode} onChange={setExecutionMode} />
            </div>
          )}
          {preview && executionOptions ? <AttachmentBatchScopeControls collapsible maxRecordCount={maxRecordCount}
            onChange={value => { setMaxRecordCount(value); setOperatorConfirmed(false); }}
            continuationId={continuationId} onContinuationChange={value => { setContinuationId(value); setOperatorConfirmed(false); }}
            previousExecutionId={previousExecutionId}
            estimate={estimateAttachmentBatchApiCalls({ scanRecordCount: estimateRecordCount,
              updateRecordCount: diagnostic || !Object.entries(executionOptions).some(([key, value]) => key !== "moveMigratedExternalSourceFilesToTrash" && (value === true || key === "externalResidualFileHandling" && value === "restore_to_record")) ? 0 : estimateRecordCount,
              // Registration reads form fields, layout and the record high-water mark once.
              fixedCheckCalls: 3 })} /> : null}
          {preview && executionOptions ? (
            <div className="flex flex-wrap items-center gap-3">
              <Button type="button" onClick={() => void handleStart()} disabled={!canStart || startExecution.isLoading}>
                {startExecution.isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />}
                {diagnostic ? "診断を開始" : "整合化を開始"}
              </Button>
              {startBlockedReason ? (
                <span className="text-sm text-muted-foreground">{startBlockedReason}</span>
              ) : null}
            </div>
          ) : null}
        </CardContent>
      </Card>
    </section>
  );
}

function PreviewPanel({
  preview,
  onPreview,
  previewLoading,
  options,
  executionMode,
  modeId,
  storageDisplayName,
  operatorConfirmed,
  placementTemplates,
  onChangePlacementTemplates,
  onChangeMode,
  onChangeOptions,
  onChangeConfirmed,
}: {
  preview: AttachmentStorageAlignmentPlanPreviewResponse;
  onPreview(): void;
  previewLoading: boolean;
  options: AttachmentStorageAlignmentExecutionOptions;
  executionMode: AttachmentStorageAlignmentExecutionMode;
  modeId: AppBindingResponse["modeId"];
  storageDisplayName?: string;
  operatorConfirmed: boolean;
  placementTemplates: SharedStorageExternalizePlacementTemplates | null;
  onChangePlacementTemplates(value: SharedStorageExternalizePlacementTemplates): void;
  onChangeMode(value: AttachmentStorageAlignmentExecutionMode): void;
  onChangeOptions(value: AttachmentStorageAlignmentExecutionOptions): void;
  onChangeConfirmed(value: boolean): void;
}) {
  const applicableCodes = new Set<string>(
    preview.executionOptionApplicability
      .filter((item) => item.applicable)
      .map((item) => item.optionCode),
  );
  const applicable = (code: string) => applicableCodes.has(code);
  const update = (patch: Partial<AttachmentStorageAlignmentExecutionOptions>) => {
    const next = { ...options, ...patch };
    if (patch.migrateExternalStorageFilesToStandardStorage === false) {
      next.moveMigratedExternalSourceFilesToTrash = false;
    }
    onChangeOptions(next);
  };
  const mutating = executionMode === "mutating_alignment";
  const optionRows = ALIGNMENT_OPTIONS.filter((option) =>
    applicable(option.code) && (!option.compatibleModeOnly || modeId === "attachment_compatible") &&
    (option.key !== "moveMigratedExternalSourceFilesToTrash" || options.migrateExternalStorageFilesToStandardStorage));
  const showResidualChoice = modeId === "attachment_compatible" &&
    applicable("external_residual_file_handling");
  const externalizeSelected = Boolean(placementTemplates) &&
    options.migrateStandardStorageFilesToExternalStorage;
  const externalTargets = preview.fieldPlans.filter(field => field.desiredStorageMethod === "external_storage");
  const hasRootTarget = externalTargets.some(field => !field.subtableFieldCode);
  const hasSubtableTarget = externalTargets.some(field => Boolean(field.subtableFieldCode));
  const selectedRisks = [
    ...(options.moveMigratedExternalSourceFilesToTrash ? ["取り込みが完了した元ファイルを隔離フォルダへ移します。元の場所からは参照できなくなります。"] : []),
    ...(options.deleteConfirmedBrokenExternalAttachmentDisplay ? ["ファイルが見つからない添付の表示をレコードから取り除きます。"] : []),
    ...(options.externalResidualFileHandling === "move_to_trash" ? ["レコードで使われていないファイルを隔離フォルダへ移します。"] : []),
    ...(options.externalResidualFileHandling === "restore_to_record" ? ["レコードで使われていないファイルを添付に追加します。以前に外した添付も戻る場合があります。"] : []),
  ];
  const fieldGroups = (["standard_attachment_fields", "external_attachment_fields"] as const)
    .map((group) => ({ group, fields: preview.fieldPlans.filter((field) => field.uiGroup === group) }))
    .filter((entry) => entry.fields.length > 0);

  return (
    <div className="space-y-4">
      {preview.blockers.length > 0 ? (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          <div className="mb-1 font-medium">開始できない条件があります</div>
          {preview.blockers.map((item) => (
            <div key={item.blockerCode}>{alignmentBlockerMessage(item)}</div>
          ))}
        </div>
      ) : null}
      {preview.warnings.length > 0 ? (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950">
          未反映の設定変更があります。今回の判定は反映済み設定を使用します。
        </div>
      ) : null}

      <h3 className="text-sm font-medium">整合化対象フィールド（合計{preview.fieldPlans.length}フィールド）</h3>
      {fieldGroups.length > 0 ? (
        <div className={`grid gap-3${fieldGroups.length > 1 ? " md:grid-cols-2" : ""}`}>
          {fieldGroups.map(({ group, fields }) => (
            <section key={group} className="rounded-md border border-border p-3 text-sm" aria-label={FIELD_GROUP_LABELS[group]}>
              <h3 className="font-medium">{FIELD_GROUP_LABELS[group]}</h3>
              <ul className="mt-2 space-y-1">
                {fields.map((field) => (
                  <li key={field.fieldBindingKey}>
                    {field.fieldLabel}（{field.fieldCode}）
                    {field.targetScopeKind === "subtable_row" ? `・テーブル: ${field.subtableFieldCode}` : "・通常フィールド"}
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      ) : null}

      <div className="space-y-2">
        <Button type="button" variant="outline" onClick={onPreview} disabled={previewLoading}>
          {previewLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ScanSearch className="mr-2 h-4 w-4" />}
          候補レコード数を確認
        </Button>
        {preview.candidateRecordTotalCount !== null ? <SummaryCell label="候補レコード" value={`${preview.candidateRecordTotalCount} 件`} /> : null}
        <p className="text-xs text-muted-foreground">確認だけではデータは変わりません。件数は確認時点の参考値です。</p>
      </div>
      <ExecutionModeChoice value={executionMode} onChange={onChangeMode} />

      {mutating ? (
        <>
          <div className="space-y-1 text-sm" aria-label="実行する処理の要約">
            <p className="font-medium">実行する処理</p>
            {options.migrateStandardStorageFilesToExternalStorage && externalTargets.length ? <div className="space-y-1">
              <p>外部化するフィールド：{externalTargets.map(field => field.fieldLabel || field.fieldCode).join("、")}</p>
              <p>移動先ストレージ：{storageDisplayName ?? "取得できません。ストレージ設定を確認してください。"}</p>
              <p>この画面の「開始」で整合化を開始します。移動完了後、元ファイルはkintone標準添付から削除されます。</p>
              <p>戻す場合はフィールドを「標準添付」に変更して反映し、整合化を実行してください。</p>
            </div> : null}
            <p>無効な特殊ファイル：{options.invalidSpecialFileHandling === "delete" ? "kintone上の対象添付を削除" : "スキップ"}</p>
            {optionRows.filter(option => options[option.key]).map(option => <p key={option.key}>{option.label}</p>)}
            {showResidualChoice ? <p>外部残存ファイル：{options.externalResidualFileHandling === "none" ? "処理しない" : options.externalResidualFileHandling === "restore_to_record" ? "レコードに取り込む" : "隔離領域へ移動"}</p> : null}
            {!optionRows.some(option => options[option.key]) && options.externalResidualFileHandling === "none" && options.invalidSpecialFileHandling !== "delete" ? <p>実行する処理が選択されていません。</p> : null}
          </div>
          {selectedRisks.length ? <div role="note" className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950"><p className="font-medium">実行前に確認してください</p>{selectedRisks.map(risk => <p key={risk}>{risk}</p>)}</div> : null}
          <details data-maintenance-options>
            <summary className="cursor-pointer py-2 text-sm">処理のオプション{JSON.stringify(options) !== JSON.stringify(defaultExecutionOptions(preview)) ? "（変更あり）" : ""}</summary>
            <div className="space-y-3 py-3">
          <fieldset className="space-y-3">
            <legend className="text-sm font-medium">実行する処理の選択</legend>
            {optionRows.length === 0 && !showResidualChoice ? (
              <p className="text-sm text-muted-foreground">この設定で実行できる整合化の処理はありません。</p>
            ) : null}
            {optionRows.map((option) => (
              <Option
                key={option.code}
                label={option.label}
                description={typeof option.description === "function" ? option.description(modeId) : option.description}
                checked={Boolean(options[option.key])}
                disabled={option.key === "moveMigratedExternalSourceFilesToTrash" && !options.migrateExternalStorageFilesToStandardStorage}
                onChange={(value) => update({ [option.key]: value })}
              />
            ))}
          </fieldset>
          <label className="block text-sm">無効な証跡・サムネイルの扱い
            <select className="ml-2 rounded border" value={options.invalidSpecialFileHandling ?? "skip"}
              onChange={event => update({ invalidSpecialFileHandling: event.target.value as "skip" | "delete" })}>
              <option value="skip">スキップ（変更せず続行）</option>
              <option value="delete">kintoneの添付から削除</option>
            </select>
          </label>
          {options.invalidSpecialFileHandling === "delete" ? <p role="note" className="text-sm text-amber-900">無効な証跡・サムネイルをkintoneの添付から取り除きます。外部ストレージのファイルは削除しません。</p> : null}

          {showResidualChoice ? (
            <ExternalResidualFileHandlingChoice
              value={options.externalResidualFileHandling}
              onChange={(value) => update({ externalResidualFileHandling: value })}
            />
          ) : null}

            </div>
          </details>
          {placementTemplates && externalizeSelected ? <p className="break-all text-sm">格納先：{hasRootTarget ? `通常 ${placementTemplates.rootFieldTemplate}` : ""}{hasRootTarget && hasSubtableTarget ? " ／ " : ""}{hasSubtableTarget ? `テーブル ${placementTemplates.subtableFieldTemplate}` : ""}</p> : null}
          <details data-maintenance-options hidden={!placementTemplates || !externalizeSelected}>
            <summary className="cursor-pointer py-2 text-sm">格納先パスのオプション{placementTemplates && (placementTemplates.rootFieldTemplate !== preview.sharedStorageExternalizePlacementDefaults?.rootFieldTemplate || placementTemplates.subtableFieldTemplate !== preview.sharedStorageExternalizePlacementDefaults?.subtableFieldTemplate) ? "（変更あり）" : ""}</summary>
          {placementTemplates && externalizeSelected ? (
            <SharedStorageExternalizePlacementEditor
              templates={placementTemplates}
              templateFields={preview.sharedStorageExternalizeTemplateFields ?? []}
              appBaseFolder={preview.sharedStorageExternalizePlacementDefaults?.appBaseFolder ?? ""}
              sampleRecords={preview.sharedStorageExternalizeSampleRecords ?? []}
              targets={preview.fieldPlans
                .filter((field) => field.desiredStorageMethod === "external_storage")
                .map((field) => ({
                  fieldBindingKey: field.fieldBindingKey,
                  fieldCode: field.fieldCode,
                  subtableFieldCode: field.subtableFieldCode ?? null,
                }))}
              onChange={onChangePlacementTemplates}
            />
          ) : null}

          </details>
          {modeId === "shared_storage" && externalizeSelected ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950">
              共有ストレージモードでは外部ストレージ上の実ファイルを移動・隔離・削除しません。
            </div>
          ) : null}

          <label className="flex items-start gap-3 rounded-md border border-border p-3 text-sm">
            <input className="mt-1" type="checkbox" checked={operatorConfirmed} disabled={preview.blockers.length > 0} onChange={(event) => onChangeConfirmed(event.target.checked)} />
            <span>対象と実行する処理を確認しました。成功済みの更新は取消で元に戻らないことを理解しています。</span>
          </label>
        </>
      ) : null}
    </div>
  );
}

function ExecutionModeChoice({ value, onChange }: {
  value: AttachmentStorageAlignmentExecutionMode;
  onChange(value: AttachmentStorageAlignmentExecutionMode): void;
}) {
  return (
    <fieldset className="space-y-2">
      <legend className="text-sm font-medium">実行方法</legend>
      <div className="grid gap-2 md:grid-cols-2">
        <ModeChoice label="診断のみ" description="状態を調べて結果を記録します。データは変更しません。" checked={value === "diagnostic_only"} onChange={() => onChange("diagnostic_only")} />
        <ModeChoice label="整合化" description="選んだ処理で修復・移行します。" checked={value === "mutating_alignment"} onChange={() => onChange("mutating_alignment")} />
      </div>
    </fieldset>
  );
}

const FIELD_GROUP_LABELS = {
  standard_attachment_fields: "標準添付として整合するフィールド",
  external_attachment_fields: "外部添付として整合するフィールド",
} as const;

type BooleanAlignmentOptionKey = {
  [Key in keyof AttachmentStorageAlignmentExecutionOptions]:
    AttachmentStorageAlignmentExecutionOptions[Key] extends boolean ? Key : never
}[keyof AttachmentStorageAlignmentExecutionOptions];

const ALIGNMENT_OPTIONS: ReadonlyArray<{
  code: string;
  key: BooleanAlignmentOptionKey;
  label: string;
  description: string | typeof storagePlacementRepairHelp;
  compatibleModeOnly?: boolean;
}> = [
  {
    code: "migrate_external_storage_files_to_standard_storage",
    key: "migrateExternalStorageFilesToStandardStorage",
    label: "外部添付を標準添付へ移行",
    description: "外部ストレージのファイルを、kintoneの標準添付として取り込みます。",
  },
  {
    code: "move_migrated_external_source_files_to_trash",
    key: "moveMigratedExternalSourceFilesToTrash",
    label: "移行済みの外部元ファイルを隔離",
    description: "標準添付へ取り込んだ後の外部ファイルを、隔離フォルダへ移します。削除はしません。",
    compatibleModeOnly: true,
  },
  {
    code: "migrate_standard_storage_files_to_external_storage",
    key: "migrateStandardStorageFilesToExternalStorage",
    label: "標準添付を外部添付へ移行",
    description: "kintoneの標準添付を外部ストレージへ移し、レコードには外部添付として残します。",
  },
  {
    code: "repair_storage_placement_drift",
    key: "repairStoragePlacementDrift",
    label: "配置のずれを修復",
    description: storagePlacementRepairHelp,
  },
  {
    code: "repair_compatible_thumbnail_carriers",
    key: "repairCompatibleThumbnailCarriers",
    label: "添付画像のサムネイルを修復",
    description: "kintoneに表示するサムネイル画像を作り直します。外部ストレージのファイルは変更しません。",
  },
  {
    code: "delete_confirmed_broken_external_attachment_display",
    key: "deleteConfirmedBrokenExternalAttachmentDisplay",
    label: "見つからないファイルの添付表示を削除",
    description: "外部ストレージに実体がないと確認できた添付の表示を、レコードから取り除きます。",
  },
];

type SharedStorageExternalizePlacementTemplates = {
  rootFieldTemplate: string;
  subtableFieldTemplate: string;
};

type SharedStorageExternalizeTemplateField = NonNullable<
  AttachmentStorageAlignmentPlanPreviewResponse["sharedStorageExternalizeTemplateFields"]
>[number];

const DEFAULT_PLACEMENT_TEMPLATES: SharedStorageExternalizePlacementTemplates = {
  rootFieldTemplate: "{recordIdParent}/{recordId}/{fieldCode}/{fileName}",
  subtableFieldTemplate: "{recordIdParent}/{recordId}/{subtableFieldCode}/{fieldCode}/{fileName}",
};

type SharedStorageExternalizeSampleRecord = NonNullable<
  AttachmentStorageAlignmentPlanPreviewResponse["sharedStorageExternalizeSampleRecords"]
>[number];

type TemplateKey = keyof SharedStorageExternalizePlacementTemplates;

const TEMPLATE_FIELD_TYPE_LABELS: Record<string, string> = {
  SINGLE_LINE_TEXT: "文字列（1行）",
  NUMBER: "数値",
  CALC: "計算",
  DATE: "日付",
  DROP_DOWN: "ドロップダウン",
  RADIO_BUTTON: "ラジオボタン",
  RECORD_NUMBER: "レコード番号",
};

function SharedStorageExternalizePlacementEditor({
  templates,
  templateFields,
  appBaseFolder,
  sampleRecords,
  targets,
  onChange,
}: {
  templates: SharedStorageExternalizePlacementTemplates;
  templateFields: SharedStorageExternalizeTemplateField[];
  appBaseFolder: string;
  sampleRecords: SharedStorageExternalizeSampleRecord[];
  targets: PlacementSampleTarget[];
  onChange(value: SharedStorageExternalizePlacementTemplates): void;
}) {
  const inputs = {
    rootFieldTemplate: useRef<HTMLInputElement>(null),
    subtableFieldTemplate: useRef<HTMLInputElement>(null),
  };
  const hasRoot = targets.some(target => !target.subtableFieldCode);
  const hasSubtable = targets.some(target => Boolean(target.subtableFieldCode));
  const [focusedTemplate, setActiveTemplate] = useState<TemplateKey>(hasRoot ? "rootFieldTemplate" : "subtableFieldTemplate");
  const activeTemplate = focusedTemplate === "rootFieldTemplate" && !hasRoot ? "subtableFieldTemplate" : focusedTemplate === "subtableFieldTemplate" && !hasSubtable ? "rootFieldTemplate" : focusedTemplate;
  const withoutRecordUnique = [...(hasRoot ? [templates.rootFieldTemplate] : []), ...(hasSubtable ? [templates.subtableFieldTemplate] : [])]
    .some((template) => !templateHasRecordUniqueElement(template, templateFields));

  function insertPlaceholder(placeholder: string) {
    const input = inputs[activeTemplate].current;
    const current = templates[activeTemplate];
    const start = input?.selectionStart ?? current.length;
    const end = input?.selectionEnd ?? start;
    const next = `${current.slice(0, start)}${placeholder}${current.slice(end)}`;
    onChange({ ...templates, [activeTemplate]: next });
    const caret = start + placeholder.length;
    requestAnimationFrame(() => {
      input?.focus();
      input?.setSelectionRange(caret, caret);
    });
  }

  const templateInput = (key: TemplateKey, label: string) => (
    <label className="block space-y-1">
      <span>{label}</span>
      <input
        ref={inputs[key]}
        className="w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-xs"
        value={templates[key]}
        maxLength={1024}
        onFocus={() => setActiveTemplate(key)}
        onChange={(event) => onChange({ ...templates, [key]: event.target.value })}
      />
    </label>
  );

  return (
    <fieldset className="space-y-3 rounded-md border border-border p-3 text-sm">
      <legend className="px-1 font-medium">外部ストレージの格納先パス</legend>
      <MaintenanceHelp label="保存先のフォルダとファイル名">通常は変更不要です。アプリの保存先フォルダ内で、どのフォルダ名・ファイル名にするかを指定します。最後は {"{fileName}"} にしてください。同名でも内容が違うファイルは、名前に番号を付けて保存します。</MaintenanceHelp>
      {hasRoot ? templateInput("rootFieldTemplate", "通常フィールド") : null}
      {hasSubtable ? templateInput("subtableFieldTemplate", "テーブル内フィールド") : null}
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" size="sm" variant="outline" onClick={() => onChange(DEFAULT_PLACEMENT_TEMPLATES)}>
          既定に戻す
        </Button>
      </div>
      <section aria-label="差し込み項目" className="space-y-2">
        <p className="text-xs text-muted-foreground">
          クリックすると「{activeTemplate === "rootFieldTemplate" ? "通常フィールド" : "テーブル内フィールド"}」のカーソル位置に挿入します。
        </p>
        <div className="flex flex-wrap gap-1">
          {PLACEMENT_BUILTIN_PLACEHOLDERS.filter(item => item.name !== "subtableFieldCode" || activeTemplate === "subtableFieldTemplate").map((item) => (
            <Button
              key={item.name}
              type="button"
              size="sm"
              variant="outline"
              disabled={item.name === "subtableFieldCode" && activeTemplate !== "subtableFieldTemplate"}
              onClick={() => insertPlaceholder(`{${item.name}}`)}
            >
              {item.label}
            </Button>
          ))}
        </div>
        {templateFields.length > 0 ? (
          <table className="w-full text-left text-xs">
            <caption className="sr-only">差し込めるkintoneフィールド（フォーム順）</caption>
            <thead className="text-muted-foreground">
              <tr><th className="py-1 font-normal">フィールド名</th><th className="font-normal">フィールドコード</th><th className="font-normal">種類</th><th /></tr>
            </thead>
            <tbody>
              {templateFields.map((field) => (
                <tr key={field.fieldCode} className="border-t border-border">
                  <td className="py-1">{field.fieldLabel}</td>
                  <td className="font-mono">{field.fieldCode}</td>
                  <td>
                    {TEMPLATE_FIELD_TYPE_LABELS[field.fieldType] ?? field.fieldType}
                    {field.unique && field.fieldType !== "RECORD_NUMBER" ? "・重複禁止" : ""}
                  </td>
                  <td className="text-right">
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      aria-label={`${field.fieldLabel}を挿入`}
                      onClick={() => insertPlaceholder(`{field:${field.fieldCode}}`)}
                    >
                      挿入
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </section>
      <p className="text-xs text-muted-foreground">
        フィールド値が空、改行を含む、または60文字を超える場合は、フォルダ名を補正します。
      </p>
      <PlacementSamples
        templates={templates}
        templateFields={templateFields}
        appBaseFolder={appBaseFolder}
        sampleRecords={sampleRecords}
        targets={targets}
      />
      {withoutRecordUnique ? (
        <div role="alert" className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-amber-950">
          <AlertTriangle className="mt-0.5 h-4 w-4" />
          <span>
            テンプレートにレコード番号、レコードID、または重複禁止のフィールドが含まれていません。1つのフォルダに大量のファイルが格納され、環境によってはアクセスが遅くなったりエラーが発生したりする可能性があります。
          </span>
        </div>
      ) : null}
    </fieldset>
  );
}

function PlacementSamples({ templates, templateFields, appBaseFolder, sampleRecords, targets }: {
  templates: SharedStorageExternalizePlacementTemplates;
  templateFields: SharedStorageExternalizeTemplateField[];
  appBaseFolder: string;
  sampleRecords: SharedStorageExternalizeSampleRecord[];
  targets: PlacementSampleTarget[];
}) {
  const records = sampleRecords.length > 0 ? sampleRecords : [fixedPlacementSampleRecord(templateFields)];
  const rows = records.flatMap((record) => targets.map((target) => ({
    record,
    target,
    template: target.subtableFieldCode ? templates.subtableFieldTemplate : templates.rootFieldTemplate,
    scope: target.subtableFieldCode ? "subtable_row" as const : "root_field" as const,
  })));
  if (rows.length === 0) return null;
  const base = appBaseFolder.replace(/\/+$/u, "");
  return (
    <section aria-label="格納先のサンプル" className="space-y-1 rounded-md bg-muted/30 p-2">
      <h4 className="text-xs font-medium">
        {sampleRecords.length > 0
          ? `格納先のサンプル（直近${sampleRecords.length}件のレコード）`
          : "格納先のサンプル（レコードがないため、フィールドの種類ごとの固定値で表示）"}
      </h4>
      <ul className="space-y-1 text-xs">
        {rows.map(({ record, target, template, scope }) => {
          const result = renderPlacementSample({ template, scope, record, target, templateFields });
          const label = `${sampleRecords.length > 0 ? `レコード ${record.recordId}` : "サンプル"}・${target.subtableFieldCode ? `${target.subtableFieldCode} / ` : ""}${target.fieldCode}`;
          return (
            <li key={`${record.recordId}:${target.fieldBindingKey}`} className="break-all">
              <span className="text-muted-foreground">{label}: </span>
              {result.ok ? (
                <code>{`${base}/${result.path}`}{result.truncated ? "（長いフォルダ名は60文字で切り詰め）" : ""}</code>
              ) : (
                <span className="text-destructive">
                  {result.reason === "field_not_found" ? "テンプレートに使えないフィールドがあります" : "テンプレートの形式が正しくありません"}
                </span>
              )}
            </li>
          );
        })}
      </ul>
      <p className="text-xs text-muted-foreground">
        同名のフォルダやファイルが既にある場合、実行時に「(n)」が付くことがあります。
      </p>
    </section>
  );
}

// Mirrors the service rule: the folder part must identify one record.
function templateHasRecordUniqueElement(
  template: string,
  templateFields: readonly SharedStorageExternalizeTemplateField[],
) {
  const folderPart = template.split("/").slice(0, -1).join("/");
  const names = [...folderPart.matchAll(/\{([^{}]+)\}/gu)].map((match) => match[1]!);
  if (names.includes("recordId")) return true;
  if (names.includes("recordIdParent") && names.includes("recordIdLower3")) return true;
  return names.some((name) => {
    if (!name.startsWith("field:")) return false;
    const field = templateFields.find((entry) => entry.fieldCode === name.slice("field:".length));
    return Boolean(field && (field.fieldType === "RECORD_NUMBER" || field.unique));
  });
}

function Option({ label, description, checked, disabled = false, onChange }: {
  label: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange(value: boolean): void;
}) {
  const descriptionId = useId();
  return (
    <div className="flex items-start gap-3 text-sm">
      <input id={`${descriptionId}-input`} className="mt-1" type="checkbox" checked={checked} disabled={disabled} onChange={(event) => onChange(event.target.checked)} />
      <div className="flex min-w-0 items-start gap-1">
        <label htmlFor={`${descriptionId}-input`}>{label}</label>
        <MaintenanceHelp label={label}>{description}</MaintenanceHelp>
      </div>
    </div>
  );
}

function ExternalResidualFileHandlingChoice({ value, onChange }: {
  value: AttachmentStorageAlignmentExecutionOptions["externalResidualFileHandling"];
  onChange(value: AttachmentStorageAlignmentExecutionOptions["externalResidualFileHandling"]): void;
}) {
  const choices = [
    { value: "none", label: "何もしない" },
    { value: "restore_to_record", label: "レコードに取り込む" },
    { value: "move_to_trash", label: "隔離領域へ移動" },
  ] as const;
  return (
    <fieldset className="space-y-2 text-sm">
      <legend className="font-medium">レコードで使われていないファイルの扱い</legend>
      <MaintenanceHelp label="レコードで使われていないファイル">添付欄から外した後も、保存先に残っているファイルがあります。通常は「何もしない」のままにしてください。</MaintenanceHelp>
      <div className="grid gap-2 md:grid-cols-3">
        {choices.map((choice) => (
          <label key={choice.value} className="flex items-center gap-2 rounded-md border border-border px-3 py-2">
            <input
              type="radio"
              name="attachmentAlignmentExternalResidualFileHandling"
              value={choice.value}
              checked={value === choice.value}
              onChange={() => onChange(choice.value)}
            />
            <span>{choice.label}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function ModeChoice({ label, description, checked, onChange }: {
  label: string;
  description: string;
  checked: boolean;
  onChange(): void;
}) {
  return (
    <label className="flex items-start gap-3 rounded-md border border-border px-3 py-2 text-sm">
      <input className="mt-1" type="radio" name="attachmentAlignmentMode" checked={checked} onChange={onChange} />
      <span><span className="font-medium">{label}</span><span className="block text-xs text-muted-foreground">{description}</span></span>
    </label>
  );
}

function SummaryCell({ label, value }: { label: string; value: string }) {
  return <div className="rounded-lg border border-border bg-muted/20 p-3"><div className="text-xs text-muted-foreground">{label}</div><div className="mt-1 font-medium">{value}</div></div>;
}

function AlertBox({ message }: { message: string }) {
  return <div role="alert" className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive"><AlertTriangle className="mt-0.5 h-4 w-4" /><span>{message}</span></div>;
}

function alignmentBlockerMessage(
  blocker: AttachmentStorageAlignmentPlanPreviewResponse["blockers"][number],
) {
  if (blocker.blockerCode === "deployed_snapshot_stale") {
    return "kintoneのフィールド設定と反映済み設定が一致しません。フィールド設定を正しく保存し、kintoneプラグイン設定へ反映してから、もう一度［候補レコード数を確認］を押してください。";
  }
  return blocker.requiredAction;
}

function defaultExecutionOptions(
  preview: AttachmentStorageAlignmentPlanPreviewResponse,
): AttachmentStorageAlignmentExecutionOptions {
  const selected = new Set(
    preview.executionOptionApplicability
      .filter((item) => item.defaultSelected && item.applicable)
      .map((item) => item.optionCode),
  );
  return {
    migrateExternalStorageFilesToStandardStorage: selected.has("migrate_external_storage_files_to_standard_storage"),
    migrateStandardStorageFilesToExternalStorage: selected.has("migrate_standard_storage_files_to_external_storage"),
    repairStoragePlacementDrift: selected.has("repair_storage_placement_drift"),
    repairCompatibleThumbnailCarriers: selected.has("repair_compatible_thumbnail_carriers"),
    moveMigratedExternalSourceFilesToTrash: selected.has("move_migrated_external_source_files_to_trash"),
    externalResidualFileHandling: "none",
    invalidSpecialFileHandling: "skip",
    deleteConfirmedBrokenExternalAttachmentDisplay: selected.has("delete_confirmed_broken_external_attachment_display"),
  };
}
